import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api, { mediaUrl, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import ProductCard from "@/components/store/ProductCard";
import { toast } from "sonner";

export default function Account() {
  const { user, ready, logout, refresh } = useAuth();
  const { ids } = useWishlist();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";
  const [orders, setOrders] = useState([]);
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [form, setForm] = useState(null);

  useEffect(() => { if (ready && !user) navigate("/login", { state: { from: "/account" } }); }, [ready, user]); // eslint-disable-line
  useEffect(() => {
    if (!user) return;
    setForm({ name: user.name || "", phone: user.phone || "", ...(user.address || {}), newsletter: user.newsletter });
    api.get("/orders/mine").then(({ data }) => setOrders(data)).catch(() => {});
  }, [user]);
  useEffect(() => {
    if (ids.length) api.get("/products").then(({ data }) => setWishlistProducts(data.filter((p) => ids.includes(p.id)))).catch(() => {});
    else setWishlistProducts([]);
  }, [ids]);

  if (!user || !form) return <div className="py-32 text-center font-heading text-3xl text-ink/40">Loading…</div>;

  const save = async () => {
    try {
      await api.put("/auth/me", {
        name: form.name, phone: form.phone, newsletter: form.newsletter,
        address: { street: form.street || "", unit: form.unit || "", city: form.city || "", state: form.state || "", zip: form.zip || "", country: form.country || "" },
      });
      await refresh(); toast.success("Details updated.");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const TABS = [["orders", "Orders"], ["details", "My Details"], ["wishlist", "Vacation Wishlist"]];

  return (
    <div className="px-6 md:px-12 py-14 mx-auto max-w-[1100px]" data-testid="account-page">
      <div className="flex justify-between items-end border-b hairline pb-6 mb-10">
        <div><p className="eyebrow mb-2">Your Account</p><h1 className="font-heading text-4xl md:text-5xl tracking-tight">Hello, {user.name?.split(" ")[0]}</h1></div>
        <button onClick={() => { logout(); navigate("/"); }} className="font-body text-[0.7rem] uppercase tracking-wide2 text-ink/60 hover:text-ink" data-testid="account-logout">Sign Out</button>
      </div>

      <div className="flex gap-6 mb-10">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setParams({ tab: k })} data-testid={`account-tab-${k}`}
                  className={`font-body uppercase tracking-wide2 text-[0.65rem] pb-2 border-b transition-colors ${tab === k ? "border-ink text-ink" : "border-transparent text-ink/50 hover:text-ink"}`}>{label}</button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="space-y-4" data-testid="account-orders">
          {orders.length === 0 ? <p className="font-heading text-2xl text-ink/50">No orders yet. <Link to="/shop" className="underline">Start shopping</Link></p> :
            orders.map((o) => (
              <div key={o.id} className="border hairline p-5">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div>
                    <p className="font-body text-sm font-medium">Order {o.order_number}</p>
                    <p className="font-body text-xs text-ink/50">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} item(s)</p>
                  </div>
                  <div className="text-right">
                    <span className="inline-block bg-cream font-body uppercase tracking-wide2 text-[0.6rem] px-3 py-1">{o.status}</span>
                    <p className="font-heading text-xl mt-1">${o.total?.toFixed(2)}</p>
                  </div>
                </div>
                {o.tracking_number && <p className="font-body text-xs text-ink/60 mt-3">Tracking: <b>{o.tracking_number}</b></p>}
              </div>
            ))}
        </div>
      )}

      {tab === "details" && (
        <div className="max-w-lg grid grid-cols-2 gap-x-5 gap-y-5" data-testid="account-details">
          <input className="input-luxe col-span-2" placeholder="Full Name" value={form.name} onChange={set("name")} />
          <input className="input-luxe col-span-2" placeholder="Phone" value={form.phone} onChange={set("phone")} />
          <input className="input-luxe col-span-2" placeholder="Street Address" value={form.street || ""} onChange={set("street")} />
          <input className="input-luxe" placeholder="Unit / Suite" value={form.unit || ""} onChange={set("unit")} />
          <input className="input-luxe" placeholder="City" value={form.city || ""} onChange={set("city")} />
          <input className="input-luxe" placeholder="State" value={form.state || ""} onChange={set("state")} />
          <input className="input-luxe" placeholder="ZIP" value={form.zip || ""} onChange={set("zip")} />
          <input className="input-luxe col-span-2" placeholder="Country" value={form.country || ""} onChange={set("country")} />
          <label className="col-span-2 flex items-center gap-3 font-body text-sm text-ink/70"><input type="checkbox" checked={!!form.newsletter} onChange={set("newsletter")} className="accent-ink w-4 h-4" />Newsletter subscribed</label>
          <button onClick={save} className="btn-primary col-span-2" data-testid="account-save">Save Details</button>
        </div>
      )}

      {tab === "wishlist" && (
        <div data-testid="account-wishlist">
          {wishlistProducts.length === 0 ? <p className="font-heading text-2xl text-ink/50">Your wishlist is empty.</p> :
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">{wishlistProducts.map((p) => <ProductCard key={p.id} product={p} />)}</div>}
        </div>
      )}
    </div>
  );
}
